import React, { useEffect, useState } from 'react';
import { CalendarIcon, MapPinIcon, ClockIcon, CurrencyRupeeIcon } from '@heroicons/react/24/outline';
import { useRecoilValue } from 'recoil';
import { ActiveEvents, pageDetails } from '../Recoil/ActiveEvents';
import { useParams } from 'react-router-dom';
import moment from 'moment';

function EventBanner() {
  const selectedEvent = useRecoilValue(pageDetails);
  const eventDetails = useRecoilValue(ActiveEvents);
  const { id } = useParams();
  const [currentEvent, setCurrentEvent] = useState(null);
  const [eventTypeDetails, setEventTypeDetails] = useState([]);
  const [showRegister, setShowRegister] = useState(true);

  useEffect(() => {
    // Get current event data
    if (eventDetails && eventDetails.length > 0) {
      const eventIndex = selectedEvent.currentIndex || 0;
      const eventFromState = eventDetails[eventIndex];
      
      if (eventFromState) {
        setCurrentEvent(eventFromState);
        // Check registration deadline for state event
        if (eventFromState.RegistrationEndDate) {
          const deadline = moment(eventFromState.RegistrationEndDate).set({ hour: 23, minute: 59, second: 0, millisecond: 0 });
          const isLastDate = moment().isSameOrBefore(deadline);
          setShowRegister(isLastDate);
        }
      }
    }

    // If event ID is in URL, fetch specific event details
    if (id) {
      let eventURI = process.env.REACT_APP_API_URL + "/Events/GetEventDetails";
      fetch(eventURI, {
        method: "POST",
        body: JSON.stringify(id),
        headers: {"Content-Type": "application/json"}
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((result) => {
        if (result && result.Events && result.Events.length > 0) {
          const fetchedEvent = result.Events[0];
          setCurrentEvent(fetchedEvent);
          setEventTypeDetails(fetchedEvent.EventDetails || []);
          // Check registration deadline for fetched event
          if (fetchedEvent.RegistrationEndDate) {
            const deadline = moment(fetchedEvent.RegistrationEndDate).set({ hour: 23, minute: 59, second: 0, millisecond: 0 });
            const isLastDate = moment().isSameOrBefore(deadline);
            setShowRegister(isLastDate);
          }
        }
      })
      .catch((error) => console.error(error));
    }
  }, [eventDetails, selectedEvent, id]);

  if (!currentEvent) {
    return (
      <div className="relative w-full h-[40vh] bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mt-20">
        <div className="text-center space-y-4 animate-fade-in">
          <div className="w-16 h-16 mx-auto mb-6">
            <div className="w-full h-full border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white">
            Loading Event Details...
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="relative w-full py-12 md:py-16 pt-16 md:pt-20 z-10 bg-cover bg-center"
      style={{ 
        backgroundImage: currentEvent.ImgUrl ? `url(${currentEvent.ImgUrl})` : 'none',
        backgroundColor: currentEvent.ImgUrl ? 'transparent' : '#f3f4f6'
      }}
    >
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-white/40 backdrop-blur-sm"></div>
      
      <div className="container-custom">
        <div className="max-w-5xl mx-auto space-y-6 bg-white/85 backdrop-blur-md p-8 md:p-10 rounded-2xl shadow-2xl border-2 border-white/40 shadow-inner hover:shadow-3xl transition-all duration-300">
          
          {/* Event Title */}
          <div className="text-center space-y-4">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-neutral-800 leading-tight animate-slide-up drop-shadow-sm">
              {currentEvent.EventName}
            </h1>
            
            {currentEvent.EventDescription && (
              <p className="text-lg md:text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed animate-slide-up animation-delay-200">
                {currentEvent.EventDescription}
              </p>
            )}
          </div>

          {/* Event Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {/* Event Date */}
            {currentEvent.EventDate && (
              <div className="flex flex-col items-center gap-3 p-4 bg-white/50 rounded-xl border border-white/30 hover:bg-white/70 transition-all duration-300">
                <CalendarIcon className="w-8 h-8 text-primary-500" />
                <div className="text-center">
                  <h3 className="text-sm font-semibold text-neutral-800 uppercase tracking-wide">Event Date</h3>
                  <p className="text-lg font-medium text-neutral-700 mt-1">
                    {moment(currentEvent.EventDate).format('MMM DD, YYYY')}
                  </p>
                </div>
              </div>
            )}

            {/* Event Location */}
            {currentEvent.EventLocation && (
              <div className="flex flex-col items-center gap-3 p-4 bg-white/50 rounded-xl border border-white/30 hover:bg-white/70 transition-all duration-300">
                <MapPinIcon className="w-8 h-8 text-secondary-500" />
                <div className="text-center">
                  <h3 className="text-sm font-semibold text-neutral-800 uppercase tracking-wide">Location</h3>
                  <p className="text-lg font-medium text-neutral-700 mt-1">
                    {currentEvent.EventLocation}
                  </p>
                </div>
              </div>
            )}

            {/* Event Time */}
            {currentEvent.EventStartTime && (
              <div className="flex flex-col items-center gap-3 p-4 bg-white/50 rounded-xl border border-white/30 hover:bg-white/70 transition-all duration-300">
                <ClockIcon className="w-8 h-8 text-accent-500" />
                <div className="text-center">
                  <h3 className="text-sm font-semibold text-neutral-800 uppercase tracking-wide">Event Time</h3>
                  <p className="text-lg font-medium text-neutral-700 mt-1">
                    {currentEvent.EventStartTime}
                  </p>
                </div>
              </div>
            )}

            {/* Registration Deadline */}
            {currentEvent.RegistrationEndDate && (
              <div className="flex flex-col items-center gap-3 p-4 bg-white/50 rounded-xl border border-white/30 hover:bg-white/70 transition-all duration-300">
                <CalendarIcon className="w-8 h-8 text-red-500" />
                <div className="text-center">
                  <h3 className="text-sm font-semibold text-neutral-800 uppercase tracking-wide">Registration Deadline</h3>
                  <p className="text-lg font-medium text-neutral-700 mt-1">
                    {moment(currentEvent.RegistrationEndDate).format('MMM DD, YYYY')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Event Types/Categories */}
          {eventTypeDetails && eventTypeDetails.length > 0 && (
            <div className="mt-8">
              <h2 className="text-2xl font-semibold text-neutral-800 mb-6 text-center">Event Categories</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {eventTypeDetails.map((eventType, index) => (
                  <div key={index} className="bg-white/60 backdrop-blur-sm p-6 rounded-xl border border-white/40 hover:bg-white/80 hover:shadow-lg transition-all duration-300">
                    <div className="space-y-3">
                      {eventType.MarathonName && (
                        <h3 className="text-xl font-semibold text-neutral-800">{eventType.MarathonName}</h3>
                      )}
                      
                      <div className="space-y-2">
                        {eventType.RunningDistance && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-neutral-600">Distance:</span>
                            <span className="text-sm text-neutral-800 font-semibold">{eventType.RunningDistance}</span>
                          </div>
                        )}
                        
                        {eventType.AgeLimit && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-neutral-600">Age Limit:</span>
                            <span className="text-sm text-neutral-800 font-semibold">{eventType.AgeLimit}</span>
                          </div>
                        )}
                        
                        {eventType.PriceAmount && (
                          <div className="flex items-center gap-2">
                            <CurrencyRupeeIcon className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-neutral-600">Fee:</span>
                            <span className="text-lg text-green-600 font-bold">₹{eventType.PriceAmount}</span>
                          </div>
                        )}

                        {eventType.StartLocation && eventType.EndLocation && (
                          <div className="flex items-center gap-2">
                            <MapPinIcon className="w-4 h-4 text-primary-500" />
                            <span className="text-sm text-neutral-800">{eventType.StartLocation} → {eventType.EndLocation}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact Information */}
          {(currentEvent.ContactPerson || currentEvent.ContactPersonMobileNo || currentEvent.ContactPersonEmailId) && (
            <div className="mt-8 p-6 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl border border-primary-200">
              <h3 className="text-xl font-semibold text-neutral-800 mb-4 text-center">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                {currentEvent.ContactPerson && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Contact Person</span>
                    <p className="text-lg font-semibold text-neutral-800">{currentEvent.ContactPerson}</p>
                  </div>
                )}
                
                {currentEvent.ContactPersonMobileNo && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Phone</span>
                    <p className="text-lg font-semibold text-neutral-800">{currentEvent.ContactPersonMobileNo}</p>
                  </div>
                )}
                
                {currentEvent.ContactPersonEmailId && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Email</span>
                    <p className="text-lg font-semibold text-neutral-800">{currentEvent.ContactPersonEmailId}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Call to Action */}
          {showRegister ? (
            <div className="text-center mt-8">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold rounded-xl shadow-lg border border-primary-400 animate-pulse">
                <span>👇 Complete Your Registration Below 👇</span>
              </div>
            </div>
          ) : (
            <div className="text-center mt-8">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-xl shadow-lg border border-red-400">
                <span>⏰ Registration Deadline Passed</span>
              </div>
              <p className="text-neutral-600 mt-3 text-lg">
                Registration for this event has ended. Please check other upcoming events.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EventBanner;